import React, { useState } from 'react'
import { Button } from '@/race-tracker/components/ui/button'
import { Input } from '@/race-tracker/components/ui/input'
import { X } from 'lucide-react'
import {
  useUpdateStationShareMarket,
  useCreateStationShareEntry,
  useUpdateStationShareEntry,
  useDeleteStationShareEntry,
  useCreateStationShareNote,
  useDeleteStationShareNote,
  useCreateChangeLog,
} from '@/big-political-list/hooks/useBplData'

function logShareChange(createLog, userName, market, descriptions, reason) {
  return createLog.mutateAsync({
    user: userName,
    action: 'updated',
    entity_type: 'StationShare',
    record_label: `${market.state} — ${market.market}`,
    field_changes: [],
    summary: descriptions.join('; '),
    ...(reason ? { reason } : {}),
  })
}

export default function StationShareCard({ market, entries, notes, canWrite = true, userName = 'User' }) {
  const updateMarket = useUpdateStationShareMarket()
  const createEntry = useCreateStationShareEntry()
  const updateEntry = useUpdateStationShareEntry()
  const deleteEntry = useDeleteStationShareEntry()
  const createNote = useCreateStationShareNote()
  const deleteNote = useDeleteStationShareNote()
  const createLog = useCreateChangeLog()

  const [editMode, setEditMode] = useState(null)
  const [editedStations, setEditedStations] = useState([])
  const [reason, setReason] = useState('')
  const [commentMode, setCommentMode] = useState(false)
  const [commentText, setCommentText] = useState('')
  const [saving, setSaving] = useState(false)
  const [commenting, setCommenting] = useState(false)

  const hasBuyRule = notes.some(n => n.kind === 'buy_rule')
  const sumPct = Math.round(market.share_sum ?? entries.reduce((s, e) => s + (e.share || 0), 0))
  const isOffSum = sumPct < 95 || sumPct > 105
  const maxShare = Math.max(...entries.map(e => e.share || 0), 1)
  const sortedEntries = [...entries].sort((a, b) => (b.share || 0) - (a.share || 0))
  const hasDeal = entries.some(e => e.deal)

  const startStructuredEdit = () => {
    setEditedStations(sortedEntries.map(e => ({ ...e, share: String(e.share), deal: !!e.deal, affiliate: e.affiliate || '' })))
    setReason('')
    setEditMode('structured')
  }

  const setStation = (idx, field, value) => {
    setEditedStations(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s))
  }

  const detectSingleFieldEdit = (oldMap) => {
    const changes = []
    for (const st of editedStations) {
      if (!st.id) continue
      const old = oldMap[st.id]
      if (!old) continue
      const shareNum = parseFloat(st.share) || 0
      if (old.share !== shareNum) changes.push('share')
      if (!!old.deal !== !!st.deal) changes.push('deal')
      if ((old.affiliate || '') !== st.affiliate) changes.push('affiliate')
    }
    const keepIds = new Set(editedStations.filter(s => s.id).map(s => s.id))
    for (const e of entries) {
      if (!keepIds.has(e.id)) changes.push('remove')
    }
    const newStations = editedStations.filter(s => !s.id)
    if (newStations.length > 0) changes.push('add')
    return changes.length === 1 ? changes[0] : null
  }

  const handleSave = async () => {
    setSaving(true)
    const now = new Date().toISOString()
    const oldMap = Object.fromEntries(entries.map(e => [e.id, e]))
    const descriptions = []
    const fieldChanges = []

    for (let i = 0; i < editedStations.length; i++) {
      const st = editedStations[i]
      const shareNum = parseFloat(st.share) || 0
      if (st.id) {
        const old = oldMap[st.id]
        if (old.share !== shareNum) {
          descriptions.push(`Changed ${st.call_sign} share from ${old.share}% to ${shareNum}%`)
          fieldChanges.push({ field: `${st.call_sign} share`, old_value: String(old.share), new_value: String(shareNum) })
        }
        if (!!old.deal !== !!st.deal) {
          descriptions.push(st.deal ? `Added share deal on ${st.call_sign}` : `Removed share deal on ${st.call_sign}`)
          fieldChanges.push({ field: `${st.call_sign} deal`, old_value: String(!!old.deal), new_value: String(!!st.deal) })
        }
        if ((old.affiliate || '') !== st.affiliate) {
          const afDesc = st.affiliate
            ? `Set ${st.call_sign} affiliate to ${st.affiliate}`
            : `Cleared ${st.call_sign} affiliate`
          descriptions.push(afDesc)
          fieldChanges.push({ field: `${st.call_sign} affiliate`, old_value: old.affiliate || '', new_value: st.affiliate })
        }
        if (old.call_sign !== st.call_sign) {
          descriptions.push(`Renamed station ${old.call_sign} to ${st.call_sign}`)
          fieldChanges.push({ field: 'call_sign', old_value: old.call_sign, new_value: st.call_sign })
        }
        await updateEntry.mutateAsync({
          id: st.id,
          data: { call_sign: st.call_sign, affiliate: st.affiliate, share: shareNum, sort_order: i + 1, deal: !!st.deal },
        })
      } else {
        await createEntry.mutateAsync({
          market_id: market.id, call_sign: st.call_sign, affiliate: st.affiliate || '',
          share: shareNum, sort_order: i + 1, deal: !!st.deal,
        })
        descriptions.push(`Added station ${st.call_sign} at ${shareNum}%`)
        fieldChanges.push({ field: 'station added', old_value: '', new_value: st.call_sign })
      }
    }

    const keepIds = new Set(editedStations.filter(s => s.id).map(s => s.id))
    for (const e of entries) {
      if (!keepIds.has(e.id)) {
        await deleteEntry.mutateAsync(e.id)
        descriptions.push(`Removed station ${e.call_sign}`)
        fieldChanges.push({ field: 'station removed', old_value: e.call_sign, new_value: '' })
      }
    }

    const newSum = Math.round(editedStations.reduce((s, e) => s + (parseFloat(e.share) || 0), 0))

    await updateMarket.mutateAsync({
      id: market.id,
      data: { share_sum: newSum, updated_by: userName, updated_at: now },
    })

    if (descriptions.length > 0) {
      await logShareChange(createLog, userName, market, descriptions, reason.trim() || undefined)
    }

    setSaving(false)
    setEditMode(null)
    setReason('')
  }

  const oldMap = Object.fromEntries(entries.map(e => [e.id, e]))
  const singleField = editMode === 'structured' ? detectSingleFieldEdit(oldMap) : null
  const showReason = singleField && ['share', 'deal', 'affiliate'].includes(singleField)

  const handleDeleteNote = async (note) => {
    await deleteNote.mutateAsync(note.id)
    await logShareChange(
      createLog, userName, market,
      [`Deleted comment: "${note.body.slice(0, 60)}${note.body.length > 60 ? '…' : ''}"`]
    )
  }

  const handleComment = async () => {
    if (!commentText.trim()) return
    setCommenting(true)
    await createNote.mutateAsync({
      market_id: market.id, kind: 'comment', body: commentText.trim(),
      author: userName, created_at: new Date().toISOString(),
    })
    await logShareChange(createLog, userName, market, ['Added comment'])
    setCommentText('')
    setCommentMode(false)
    setCommenting(false)
  }

  return (
    <div className="rounded-lg border bg-card overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground truncate">{market.market}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            <span className="font-medium text-foreground">{market.state}</span>
            {' · '}{entries.length} station{entries.length !== 1 ? 's' : ''}
            {' · '}
            <span className={isOffSum ? 'text-amber-600 font-medium' : ''}>{sumPct}%</span>
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0 flex-wrap justify-end">
          {isOffSum && (
            <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-700 border border-amber-200">
              sum {sumPct}%
            </span>
          )}
          {hasBuyRule && (
            <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-100 text-blue-700 border border-blue-200">
              buy-rule
            </span>
          )}
          {hasDeal && (
            <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">
              deal
            </span>
          )}
          {canWrite && (
            <>
              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={startStructuredEdit}>Edit</Button>
              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setCommentMode(v => !v)}>Comment</Button>
            </>
          )}
        </div>
      </div>

      {/* Station bars — display mode */}
      {!editMode && (
        <div className="px-4 pb-3 space-y-1.5">
          {sortedEntries.map(e => {
            const pct = e.share || 0
            const isDeal = !!e.deal
            return (
              <div key={e.id} className="flex items-center gap-2">
                <div className="w-28 flex items-center gap-1 shrink-0 min-w-0">
                  {isDeal && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />}
                  <span className={`text-xs font-mono truncate ${isDeal ? 'font-bold text-foreground' : 'text-muted-foreground'}`}>
                    {e.call_sign}{e.affiliate ? ` (${e.affiliate})` : ''}
                  </span>
                </div>
                <div className="flex-1 h-3.5 bg-muted rounded-sm overflow-hidden">
                  <div
                    className={`h-full rounded-sm ${isDeal ? 'bg-emerald-500' : 'bg-primary/35'}`}
                    style={{ width: `${(pct / maxShare) * 100}%` }}
                  />
                </div>
                <span className={`w-8 text-xs text-right shrink-0 tabular-nums ${isDeal ? 'font-bold text-foreground' : 'text-muted-foreground'}`}>
                  {pct}%
                </span>
              </div>
            )
          })}
        </div>
      )}

      {/* Structured edit mode */}
      {editMode === 'structured' && (
        <div className="px-4 pb-3 space-y-1.5">
          <p className="text-[11px] text-muted-foreground mb-2">Call sign · Affiliate · Share%</p>
          {editedStations.map((st, idx) => (
            <div key={st.id || `new-${idx}`} className="flex items-center gap-2 flex-wrap">
              <Input
                value={st.call_sign}
                onChange={e => setStation(idx, 'call_sign', e.target.value)}
                className="h-7 text-xs font-mono w-24 shrink-0"
                placeholder="WXYZ"
              />
              <Input
                value={st.affiliate}
                onChange={e => setStation(idx, 'affiliate', e.target.value)}
                className="h-7 text-xs w-16 shrink-0"
                placeholder="ABC"
              />
              <Input
                type="number" min={0} max={100}
                value={st.share}
                onChange={e => setStation(idx, 'share', e.target.value)}
                className="h-7 text-xs w-16 shrink-0"
              />
              <span className="text-xs text-muted-foreground">%</span>
              <label className="flex items-center gap-1 text-xs text-muted-foreground cursor-pointer select-none shrink-0">
                <input
                  type="checkbox"
                  checked={!!st.deal}
                  onChange={e => setStation(idx, 'deal', e.target.checked)}
                  className="accent-emerald-600 w-3 h-3"
                />
                Share deal
              </label>
              <button
                onClick={() => setEditedStations(prev => prev.filter((_, i) => i !== idx))}
                className="text-destructive hover:text-destructive/80 text-base leading-none px-1"
              >×</button>
            </div>
          ))}
          <Button
            size="sm" variant="outline" className="h-7 text-xs mt-1"
            onClick={() => setEditedStations(prev => [...prev, { call_sign: '', affiliate: '', share: '0', sort_order: prev.length + 1 }])}
          >+ Add Station</Button>

          {showReason && (
            <Input
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Reason for change (optional)"
              className="h-7 text-xs mt-1"
            />
          )}

          <div className="flex gap-2 pt-1">
            <Button size="sm" onClick={handleSave} disabled={saving} className="h-7 text-xs bg-primary text-primary-foreground hover:bg-primary/90">
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setEditMode(null); setReason('') }}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Notes */}
      {notes.length > 0 && !editMode && (
        <div className="border-t px-4 py-2.5 space-y-1.5">
          {notes.map(n => (
            <div
              key={n.id}
              className={`group text-xs px-2.5 py-1.5 rounded flex items-start justify-between gap-2 ${
                n.kind === 'buy_rule'
                  ? 'bg-blue-50 text-blue-800 border border-blue-200'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              <span>
                {n.kind === 'buy_rule' && <span className="font-semibold mr-1">BUY RULE:</span>}
                {n.body}
              </span>
              {n.kind === 'comment' && canWrite && (
                <button
                  onClick={() => handleDeleteNote(n)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0 hover:text-destructive"
                  title="Delete comment"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Comment input */}
      {commentMode && (
        <div className="border-t px-4 py-3 space-y-2">
          <textarea
            value={commentText}
            onChange={e => setCommentText(e.target.value)}
            placeholder="Add a comment…"
            className="w-full border rounded-md px-3 py-2 text-xs resize-none focus:outline-none focus:ring-2 focus:ring-ring bg-background"
            rows={2}
          />
          <div className="flex gap-2">
            <Button
              size="sm" onClick={handleComment} disabled={!commentText.trim() || commenting}
              className="h-7 text-xs bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {commenting ? 'Saving…' : 'Add Comment'}
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setCommentMode(false); setCommentText('') }}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
